// Survey editor validation, kept out of the Formik controller so it imports (and tests)
// without Firebase, Sentry or React.

import * as Yup from 'yup';
import {
    ContinuePromptQuestion,
    Interview,
    MultipleChoiceQuestion,
    PuppetAnimationKey,
    PuppetQuestion,
    Question,
    QuestionType,
} from '@synergetx/puppet-interview-types';

/**
 * The languages the survey is authored in. `from[2]` is the interview for a test nested inside a
 * question; the root `context` fallback keeps this working if the nesting depth changes.
 */
function languagesFromContext(context: Yup.TestContext): string[] {
    const fromInterview = (context.from?.[2]?.value as Partial<Interview> | undefined)?.languages;
    const fromRoot = (context.options.context as Partial<Interview> | undefined)?.languages;

    return fromInterview ?? fromRoot ?? [];
}

/**
 * Fails unless `missingFieldsFor` is empty for *every* language, naming those that fall short.
 * Must combine across languages, not assign — assigning lets one filled-in language mask the
 * rest and publishes a survey the tablet app cannot administer.
 */
function everyLanguageComplete(
    context: Yup.TestContext,
    label: string,
    missingFieldsFor: (language: string) => string[],
): boolean | Yup.ValidationError {
    const languages = languagesFromContext(context);

    // An empty language list is the `languages` field's own error to report.
    if (!languages.length) return true;

    const incomplete = languages
        .map((language) => ({ language, missing: missingFieldsFor(language) }))
        .filter(({ missing }) => missing.length > 0);

    if (!incomplete.length) return true;

    const detail = incomplete
        .map(({ language, missing }) => `${language}: ${missing.join(', ')}`)
        .join('; ');

    return context.createError({ message: `${label} incomplete — ${detail}` });
}

export const interviewSchema: Yup.ObjectSchema<Interview> = Yup.object({
    accessPIN: Yup.string().nullable(),
    archived: Yup.boolean(),
    createdAt: Yup.number().required(),
    groupId: Yup.string().required(),
    id: Yup.string()
        .label('name')
        .required('Interview ID is required')
        .min(1, 'Interview ID is required'),
    interviewGeneralAudio: Yup.object().when('languages', (variables) => {
        const languages: string[] = variables[0] || [];
        const languageSchemas = languages.reduce(
            (acc, language) => {
                acc[language] = Yup.object({
                    thatsLikeMe: Yup.string()
                        .min(3, `[${language}] "Thats like me" audio required`)
                        .required(`[${language}] "Thats like me" audio required`),
                    meToo: Yup.string()
                        .min(3, `[${language}] "Me too" audio required`)
                        .required(`[${language}] "Me too" audio required`),
                    whichOneOfUs: Yup.string()
                        .min(3, `[${language}] "Which one of us is like you" audio required`)
                        .required(`[${language}] "Which one of us is like you" audio required`),
                });
                return acc;
            },
            {} as Record<
                string,
                Yup.ObjectSchema<{ thatsLikeMe: string; meToo: string; whichOneOfUs: string }>
            >,
        );

        return Yup.object(languageSchemas);
    }),
    isEditingLocked: Yup.boolean().required(),
    languages: Yup.array()
        .label('Languages')
        .required('Must have a language')
        .min(1, 'Must have a language'),
    name: Yup.string()
        .label('name')
        .required('Survey name is required')
        .min(1, 'Survey name is required'),
    puppetAnimation: Yup.string<PuppetAnimationKey>().required(),
    questions: Yup.array()
        .of<Question>(
            Yup.lazy((question: Question) => {
                const { type } = question;

                // Assigned on every branch below; no initializer needed.
                let yupObj: Yup.AnyObjectSchema;

                const nameValidation = Yup.string()
                    .required('A name is required')
                    .test('is-unique', 'Question name is not unique', (value, context) => {
                        const position = context.parent.position;
                        const options = [...(context.options.context?.questions as Question[])];
                        const matchingName =
                            options.filter(({ name }) => value === name).length > 1;

                        if (matchingName) {
                            const indexOfMatching =
                                options
                                    .filter((question) => question.position !== position)
                                    .find(({ name }) => name === value)?.position || 0;
                            return context.createError({
                                path: context.path,
                                message: `Question name is the same as question ${
                                    indexOfMatching + 1
                                }`,
                                params: {},
                            });
                        } else {
                            return !!value;
                        }
                    });

                if (type === QuestionType.PUPPET) {
                    yupObj = Yup.object<Question>({
                        name: nameValidation,
                        position: Yup.number().required(),
                        type: Yup.string().required(),
                        puppet: Yup.object().test(
                            'no empty audio urls or text',
                            'Every puppet question must have a left and right audio url and text',
                            (_value, context) =>
                                everyLanguageComplete(context, 'Puppet content', (language) => {
                                    const content = (question as PuppetQuestion)?.localeContent?.[
                                        language
                                    ];
                                    if (!content) return ['nothing entered'];

                                    const missing: string[] = [];
                                    if (!content.leftAudioAsset?.url) missing.push('left audio');
                                    if (!content.rightAudioAsset?.url) missing.push('right audio');
                                    if (!content.leftText) missing.push('left text');
                                    if (!content.rightText) missing.push('right text');

                                    return missing;
                                }),
                        ),
                    });
                } else {
                    const promptTest = Yup.object().test(
                        'no-empty-questions',
                        'Each language must have a prompt',
                        (_value, context) =>
                            everyLanguageComplete(context, 'Prompt', (language) =>
                                question?.localeContent?.[language]?.content?.length
                                    ? []
                                    : ['no text entered'],
                            ),
                    );
                    yupObj = Yup.object<Question>({
                        name: nameValidation,
                        content: promptTest,
                        position: Yup.number().required(),
                        type: Yup.string().required(),
                    });
                    if (type === QuestionType.MULTIPLE) {
                        yupObj = Yup.object<Question>({
                            name: nameValidation,
                            content: promptTest,
                            position: Yup.number().required(),
                            type: Yup.string().required(),
                            choices: Yup.object().test(
                                'no-empty-questions',
                                'All options in multiple choice question must have text',
                                (_value, context) =>
                                    everyLanguageComplete(context, 'Answer options', (language) => {
                                        const choices = (question as MultipleChoiceQuestion)
                                            ?.localeContent?.[language]?.choices;
                                        if (!choices?.length) return ['no options'];

                                        // The inner check was a `reduce` that also overwrote its
                                        // accumulator, so only the last option was ever tested.
                                        const blank = choices.filter(
                                            (choice) => !choice?.length,
                                        ).length;

                                        return blank
                                            ? [`${blank} blank option${blank > 1 ? 's' : ''}`]
                                            : [];
                                    }),
                            ),
                        });
                    }
                    if (type === QuestionType.INFO) {
                        yupObj = Yup.object<Question>({
                            name: nameValidation,
                            content: promptTest,
                            position: Yup.number().required(),
                            type: Yup.string().required(),
                        });
                    }
                    if (type === QuestionType.CONTINUE) {
                        yupObj = Yup.object<Question>({
                            name: nameValidation,
                            content: promptTest,
                            continueButtons: Yup.object().test(
                                'no-empty-buttons',
                                'All buttons in Continue Interview Prompt must have text',
                                (_value, context) =>
                                    everyLanguageComplete(context, 'Prompt buttons', (language) => {
                                        const content = (question as ContinuePromptQuestion)
                                            ?.localeContent?.[language];
                                        if (!content) return ['nothing entered'];

                                        const missing: string[] = [];
                                        if (!content.continueButtonTitle?.length)
                                            missing.push('continue title');
                                        if (!content.continueButtonDescription?.length)
                                            missing.push('continue description');
                                        if (!content.previousButtonTitle?.length)
                                            missing.push('previous title');
                                        if (!content.previousButtonDescription?.length)
                                            missing.push('previous description');
                                        if (!content.endInterviewButtonTitle?.length)
                                            missing.push('end interview title');
                                        if (!content.endInterviewButtonDescription?.length)
                                            missing.push('end interview description');

                                        return missing;
                                    }),
                            ),
                            position: Yup.number().required(),
                            type: Yup.string().required(),
                        });
                    }
                }
                return yupObj;
            }),
        )
        .required(),
    updatedAt: Yup.number().required(),
});
